import { defer, finalize, from, map, of, tap, type Observable } from 'rxjs'
import { getEasing, SPRING_SMOOTH } from '@schmancy/utils/animation'
import { reducedMotion$ } from './reduced-motion'
import { capturePreflightStyle, createLayoutSeat } from './seat'

const FLIGHT_MS = 420
const PULSE_MS = 180

const centreOf = (rect: DOMRect): { x: number; y: number } => ({
	x: rect.left + rect.width / 2,
	y: rect.top + rect.height / 2,
})

/**
 * Collapse the real source host into a destination without moving it out of its
 * authored DOM tree. The browser's popover top layer supplies visual escape;
 * where that native primitive is unavailable the same host animates in place.
 */
export function absorb(source: HTMLElement, target: HTMLElement): Observable<void> {
	return defer(() => {
		if (reducedMotion$.value) return of(undefined)

		const rect = source.getBoundingClientRect()
		const start = centreOf(rect)
		const end = centreOf(target.getBoundingClientRect())
		const dx = end.x - start.x
		const dy = end.y - start.y
		const reach = Math.max(Math.abs(dx), Math.abs(dy))
		const horizontal = Math.abs(dx) >= Math.abs(dy)
		const previousStyle = source.getAttribute('style')
		const previousPopover = source.getAttribute('popover')
		const preflight = capturePreflightStyle(source)
		const seat = createLayoutSeat(rect, preflight, 'data-schmancy-absorb-seat')

		let wasPopoverOpen = false
		let openedPopover = false
		let promoted = false
		let cleaned = false
		let supportsPopover = typeof source.showPopover === 'function' && typeof source.hidePopover === 'function'

		if (supportsPopover) {
			try {
				wasPopoverOpen = source.matches(':popover-open')
			} catch {
				supportsPopover = false
			}
		}

		if (supportsPopover && source.parentNode) {
			source.before(seat)
			Object.assign(source.style, {
				position: 'fixed',
				inset: 'auto',
				left: `${rect.left}px`,
				top: `${rect.top}px`,
				boxSizing: 'border-box',
				width: `${rect.width}px`,
				height: `${rect.height}px`,
				maxWidth: 'none',
				maxHeight: 'none',
				margin: '0',
				padding: preflight.padding,
				border: preflight.border,
				background: preflight.background,
				color: preflight.color,
				display: preflight.display,
				overflow: preflight.overflow,
			})
			try {
				if (!wasPopoverOpen) {
					source.setAttribute('popover', 'manual')
					source.showPopover()
					openedPopover = true
				}
				promoted = source.matches(':popover-open')
				if (!promoted) throw new Error('popover promotion was refused')
			} catch {
				if (openedPopover && source.matches(':popover-open')) source.hidePopover()
				seat.remove()
				if (previousPopover === null) source.removeAttribute('popover')
				else source.setAttribute('popover', previousPopover)
				if (previousStyle === null) source.removeAttribute('style')
				else source.setAttribute('style', previousStyle)
			}
		}

		Object.assign(source.style, {
			pointerEvents: 'none',
			transformOrigin:
				reach === 0 ? '50% 50%' : `${50 + (dx / reach) * 50}% ${50 + (dy / reach) * 50}%`,
			willChange: 'transform, opacity, border-radius',
		})

		const neck = horizontal ? 'scale(0.72, 0.42)' : 'scale(0.42, 0.72)'
		const pinch = horizontal ? 'scale(0.42, 0.16)' : 'scale(0.16, 0.42)'
		const flight = source.animate(
			[
				{ offset: 0, transform: 'translate(0px, 0px) scale(1)', opacity: 1, borderRadius: preflight.borderRadius },
				{
					offset: 0.3,
					transform: `translate(${dx * 0.16}px, ${dy * 0.16}px) scale(1)`,
					opacity: 1,
					borderRadius: preflight.borderRadius,
				},
				{
					offset: 0.58,
					transform: `translate(${dx * 0.5}px, ${dy * 0.5}px) ${neck}`,
					opacity: 0.94,
					borderRadius: '32%',
				},
				{
					offset: 0.82,
					transform: `translate(${dx * 0.82}px, ${dy * 0.82}px) ${pinch}`,
					opacity: 0.52,
					borderRadius: '44%',
				},
				{ offset: 1, transform: `translate(${dx}px, ${dy}px) scale(0.1)`, opacity: 0, borderRadius: '50%' },
			],
			{ duration: FLIGHT_MS, easing: getEasing(SPRING_SMOOTH), fill: 'forwards' },
		)
		const pulse = target.animate(
			[{ transform: 'scale(1)' }, { offset: 0.5, transform: 'scale(1.035)' }, { transform: 'scale(1)' }],
			{
				duration: PULSE_MS,
				delay: FLIGHT_MS - PULSE_MS,
				easing: getEasing(SPRING_SMOOTH),
			},
		)

		const restore = (): void => {
			if (cleaned) return
			cleaned = true
			flight.cancel()
			pulse.cancel()
			seat.remove()
			if (openedPopover && source.matches(':popover-open')) source.hidePopover()
			if (previousPopover === null) source.removeAttribute('popover')
			else source.setAttribute('popover', previousPopover)
			if (previousStyle === null) source.removeAttribute('style')
			else source.setAttribute('style', previousStyle)
			if (wasPopoverOpen && !source.matches(':popover-open')) {
				try {
					source.showPopover()
				} catch {
					// An externally detached authored popover cannot be reopened.
				}
			}
		}

		return from(Promise.all([flight.finished, pulse.finished])).pipe(
			tap(restore),
			map((): void => undefined),
			finalize(restore),
		)
	})
}
